import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import Select from "react-select";
import axios from "axios";
import apiUrl from "../../components/api";

const ChargeForm = ({ onClose, onSuccess, selectedProperty, selectedInvoice, readOnly=false, selectedAmount, selectedRef, selectedModel, selectedAutoGenerated, selectedNotes }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Form data
  const [amount, setAmount] = useState(selectedAmount || "");
  const [invoice, setInvoice] = useState(selectedInvoice || null);
  const [model, setModel] = useState(selectedModel || null);
  const [refOfModel, setRefOfModel] = useState(selectedRef || null);
  const [property, setProperty] = useState(selectedProperty || null);
  const [autoGenerated, setAutoGenerated] = useState(selectedAutoGenerated || false);
  const [notes, setNotes] = useState(selectedNotes || "");
  
  // Options for dropdowns
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([
    { value: "Action", label: "Action" },
    { value: "PoolAction", label: "Pool Action" },
    { value: "Contract", label: "Contract" },

  ]);
  const [refOptions, setRefOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.token;

  useEffect(() => {


    // If property is provided, no need to fetch properties
    if (selectedProperty) {
      setProperty({ value: selectedProperty._id, label: selectedProperty.name });
      // Fetch invoices for this property
      fetchInvoices(selectedProperty._id);
    } else {
      fetchProperties();
    }
    
    // If invoice is provided, set it
    if (selectedInvoice) {
      setInvoice({ value: selectedInvoice._id, label: selectedInvoice.invoice_number });
    }
  }, [selectedProperty, selectedInvoice]);
  
  // When property changes, fetch invoices for that property
  useEffect(() => {
    if (property?.value) {
      fetchInvoices(property.value);
    }
  }, [property]);
  
  // When model changes, fetch references for that model
  useEffect(() => {
    if (model?.value && property?.value) {
      fetchReferences(model.value, property.value);
    }
  }, [model, property]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/property/getPropertiesNames`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const propertyOpts = response.data.propertiesNames.map((prop) => ({
        value: prop._id,
        label: prop.name,
      }));
      
      setPropertyOptions(propertyOpts);
    } catch (err) {
      setError("Failed to fetch properties. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async (propertyId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/invoice/property/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const invoiceOpts = response.data.invoices.map((inv) => ({
        value: inv._id,
        label: inv.invoice_number,
      }));
      
      setInvoiceOptions(invoiceOpts);
    } catch (err) {
      setError("Failed to fetch invoices. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferences = async (modelType, propertyId) => {
    setLoading(true);
    try {
      let endpoint;
      switch (modelType) {
        case "Action":
          endpoint = `/action/property/${propertyId}`;
          break;
        case "PoolAction":
          endpoint = `/pool-action/property/${propertyId}`;
          break;
        case "Contract":
          endpoint = `/contract/property/${propertyId}`;
          break;
        default:
          setRefOptions([]);
          setLoading(false);
          return;
      }
      
      const response = await axios.get(`${apiUrl}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Format will differ based on model type
      const items = response.data[modelType === "Action" ? "actions" : 
                            modelType === "PoolAction" ? "poolActions" : "contracts"];
      
      const refOpts = items.map((item) => ({
        value: item._id,
        label: item.name || item.title || `${modelType} ${item._id.substring(0, 8)}...`,
      }));
      
      setRefOptions(refOpts);
    } catch (err) {
      setError(`Failed to fetch references for ${modelType}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - only amount and property are required now
    if (!amount || !property) {
      setError("Please enter an amount and select a property.");
      return;
    }
    
    setSubmitting(true);
    setError("");
    setSuccess("");
    
    try {
      // Prepare request data - only include optional fields if they're provided
      const chargeData = {
        amount: parseFloat(amount),
        property: property.value,
        auto_generated: autoGenerated,
        notes: notes,
      };
      
      // Add optional fields only if they exist
      if (invoice) {
        chargeData.invoice = invoice.value;
      }
      
      if (model) {
        chargeData.model = model.value;
      }
      
      if (refOfModel) {
        chargeData.ref_of_model = refOfModel.value;
      }
      
      const response = await axios.post(
        `${apiUrl}/charge`,
        chargeData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      setSuccess("Charge created successfully!");
      setTimeout(() => {
        if (onSuccess) onSuccess(response.data.charge);
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating charge:', err);
      setError(
        err.response?.data?.message || 
        "Failed to create charge. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Amount*</Typography>
          <TextField
            fullWidth
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            inputProps={{ min: "0", step: "0.01" }}
            disabled={submitting || readOnly}
            required
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1">Property*</Typography>
          <Select
            value={property}
            onChange={setProperty}
            options={propertyOptions}
            isDisabled={submitting || !!selectedProperty || readOnly}
            placeholder="Select Property"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 100001 }),
              menu: base => ({ ...base, zIndex: 100001 }),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1">Invoice</Typography>
          <Select
            value={invoice}
            onChange={setInvoice}
            options={invoiceOptions}
            isDisabled={submitting || !property || !!selectedInvoice || readOnly}
            placeholder="Select Invoice (Optional)"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 100001 }),
              menu: base => ({ ...base, zIndex: 100001 }),
            }}
            isClearable
            readOnly={readOnly}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1">Model Type</Typography>
          <Select
            value={model}
            onChange={setModel}
            options={modelOptions}
            isDisabled={submitting || !property || readOnly}
            placeholder="Select Model Type (Optional)"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 100001 }),
              menu: base => ({ ...base, zIndex: 100001 }),
            }}
            isClearable
            readOnly={readOnly}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1">Reference</Typography>
          <Select
            value={refOfModel}
            onChange={setRefOfModel}
            options={refOptions}
            isDisabled={submitting || !model || !property || readOnly}
            placeholder="Select Reference (Optional)"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 100001 }),
              menu: base => ({ ...base, zIndex: 100001 }),
            }}
            isClearable

          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1">Notes</Typography>
          <TextField
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter notes (Optional)"
            disabled={submitting || readOnly}
          />
        </Grid>
        
        {
          readOnly &&
          <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={autoGenerated}
                onChange={(e) => setAutoGenerated(e.target.checked)}
                disabled={submitting || readOnly}
              />
            }
            label="Auto Generated"
          />
        </Grid>}
      </Grid>
      
    {
    !readOnly &&
    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={submitting || loading}
        >
          {submitting ? <CircularProgress size={24} /> : "Create Charge"}
        </Button>
      </Box>}
    </Box>
  );
};

export default ChargeForm;